import { z } from 'zod';
import { checkFieldDefinitions, fieldDefinitionSchema, type FieldDefinition } from './fields';

// BLOCKS — owner-editable building blocks of a site (Shopify's sections,
// blocks and app embeds), as real React components over the owner's own code.
//
//   section  a full-width part of a page (hero, pricing, product grid…). Lives
//            in a section group; the owner adds, reorders, hides and edits it.
//   block    a smaller piece that lives inside a section that accepts blocks
//            (a testimonial card, a FAQ item, a "buy" button).
//   embed    rendered once per page, outside any section (chat bubble,
//            announcement bar, cookie notice). Owners toggle it.
//
// In the app:
//   frontend/src/blocks/<ns>/<name>.tsx         the component (default export)
//   frontend/src/blocks/<ns>/<name>.block.json  its schema (BlockSchema below)
//   frontend/src/content/sections.json          groups → ordered instances + settings
//   frontend/src/dunea/blocks.tsx               runtime: <SectionGroup id>, <BlockEmbeds/>
//
// `<ns>` is `local` for blocks the AI writes for this app, `theme` for the
// applied theme's sections, and the listing slug for marketplace plugins.
// Settings live in the repo (sections.json), so edits are drafts in preview
// and ship with the next publish, exactly like code.
//
// Marketplace block code runs on the OWNER's site, not in Dunea. It is limited
// to an import allowlist, checked statically (BLOCK_CODE_RULES) and always
// reviewed by a person.

export const BLOCK_KINDS = ['section', 'block', 'embed'] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

/** Imports block code may use. Anything else fails the check. */
export const BLOCK_IMPORT_ALLOWLIST = ['react', '@dunea/blocks', 'lucide-react', 'clsx'] as const;

/** Page types a section group (or theme preset) can target. */
export const PAGE_TYPES = [
  'home',
  'page',
  'product',
  'collection',
  'cart',
  'blog',
  'article',
  'funnel_step',
  'any',
] as const;
export type PageType = (typeof PAGE_TYPES)[number];

const handle = z.string().regex(/^[a-z][a-z0-9-]{1,40}$/);

export const blockSchemaSchema = z.object({
  name: handle,
  title: z.string().min(2).max(60),
  kind: z.enum(BLOCK_KINDS),
  description: z.string().max(300).optional(),
  /** Owner-editable settings; the panel renders a form from these. */
  settings: z.array(fieldDefinitionSchema).max(40).default([]),
  /** For sections: which blocks it accepts as children ('*' = any block). */
  accepts: z
    .array(z.union([z.literal('*'), z.string().max(90)]))
    .max(30)
    .default([]),
  maxBlocks: z.number().int().min(1).max(50).optional(),
  /** Where it makes sense; the section picker filters on this. */
  pageTypes: z.array(z.enum(PAGE_TYPES)).default(['any']),
  /** Starting points shown in the "add section" picker. */
  presets: z
    .array(
      z.object({
        name: z.string().min(2).max(60),
        settings: z.record(z.string(), z.unknown()).default({}),
        blocks: z
          .array(z.object({ type: z.string().max(90), settings: z.record(z.string(), z.unknown()).default({}) }))
          .max(20)
          .default([]),
      }),
    )
    .max(10)
    .default([]),
});

export type BlockSchema = z.infer<typeof blockSchemaSchema>;
export type BlockSchemaInput = z.input<typeof blockSchemaSchema>;

/** A block shipped in a plugin or theme manifest: schema + component source. */
export const blockPackageSchema = blockSchemaSchema.extend({
  /** TSX source of the component (default export). The CLI inlines it from `codeFile`. */
  code: z.string().min(20).max(60_000),
});

export type BlockPackage = z.infer<typeof blockPackageSchema>;

export function checkBlockSchemas(blocks: readonly BlockSchema[], report: (message: string) => void): void {
  const names = new Set<string>();

  for (const b of blocks) {
    if (names.has(b.name)) {
      report(`duplicate block "${b.name}"`);
    }

    names.add(b.name);
    checkFieldDefinitions(b.settings as FieldDefinition[], report, `block "${b.name}"`);

    if (b.kind !== 'section' && b.accepts.length) {
      report(`block "${b.name}": only sections can accept child blocks`);
    }
  }
}

// ─── sections.json ───

export const blockInstanceSchema: z.ZodType<BlockInstance> = z.lazy(() =>
  z.object({
    id: z.string().regex(/^[a-zA-Z0-9_-]{1,60}$/),
    /** `<ns>/<name>`, e.g. "local/hero" or "acme-reviews/stars". */
    type: z.string().regex(/^[a-z0-9-]{2,41}\/[a-z][a-z0-9-]{1,40}$/),
    settings: z.record(z.string(), z.unknown()).default({}),
    hidden: z.boolean().optional(),
    blocks: z.array(blockInstanceSchema).max(50).optional(),
  }),
);

export interface BlockInstance {
  id: string;
  type: string;
  settings: Record<string, unknown>;
  hidden?: boolean;
  blocks?: BlockInstance[];
}

export const sectionsFileSchema = z.object({
  version: z.literal(1),
  /** Section groups by id ("home", "product", "footer"…). Each is an ordered list. */
  groups: z.record(
    z.string().regex(/^[a-zA-Z0-9_-]{1,60}$/),
    z.object({
      pageType: z.enum(PAGE_TYPES).default('any'),
      sections: z.array(blockInstanceSchema).max(40),
    }),
  ),
  /** App embeds: type → enabled + settings. */
  embeds: z
    .record(z.string(), z.object({ enabled: z.boolean(), settings: z.record(z.string(), z.unknown()).default({}) }))
    .default({}),
});

export type SectionsFile = z.infer<typeof sectionsFileSchema>;

export const SECTIONS_FILE_PATH = 'frontend/src/content/sections.json';
export const BLOCKS_DIR = 'frontend/src/blocks';

/**
 * Static rules for marketplace block code. Human review still applies to every block.
 * Ids are DUN-B1xx (DUN-B001/B002 are the billing rules in REVIEW_RULES).
 */
export const BLOCK_CODE_RULES: { id: string; pattern: RegExp; message: string }[] = [
  { id: 'DUN-B101', pattern: /\beval\s*\(|\bnew\s+Function\s*\(/, message: 'eval / new Function is not allowed' },
  { id: 'DUN-B102', pattern: /\bimport\s*\(/, message: 'dynamic import() is not allowed' },
  {
    id: 'DUN-B103',
    pattern: /\b(fetch|XMLHttpRequest|WebSocket|EventSource|sendBeacon)\b/,
    message: 'network access is not allowed in block code; read data through @dunea/blocks hooks',
  },
  {
    id: 'DUN-B104',
    pattern: /dangerouslySetInnerHTML|\.innerHTML\s*=|outerHTML|insertAdjacentHTML/,
    message: 'raw HTML injection is not allowed; use <RichText>',
  },
  {
    id: 'DUN-B105',
    pattern: /document\.cookie|localStorage|sessionStorage|indexedDB/,
    message: 'storage and cookie access is not allowed',
  },
  {
    id: 'DUN-B106',
    pattern: /(window|document|globalThis)\s*\.\s*location\s*=|location\.(href|assign|replace)\b/,
    message: 'navigation is not allowed; use <Link>',
  },
  { id: 'DUN-B107', pattern: /<script\b|<iframe\b/i, message: 'script and iframe elements are not allowed' },
  // Code shapes, not bare words: "Our process" is ordinary section copy.
  {
    id: 'DUN-B108',
    pattern: /\bprocess\s*\.\s*[a-zA-Z_]|\brequire\s*\(|\b__dirname\b/,
    message: 'Node globals are not available in block code',
  },
];

/** Import specifiers in a TSX source (static imports and re-exports). */
export function importSpecifiers(code: string): string[] {
  const out: string[] = [];
  const re = /(?:^|\n)\s*(?:import|export)\s[^'"]*?from\s*['"]([^'"]+)['"]|(?:^|\n)\s*import\s*['"]([^'"]+)['"]/g;
  let m: RegExpExecArray | null;

  while ((m = re.exec(code))) {
    out.push(m[1] ?? m[2]);
  }

  return out;
}

/** Problems with block code; empty when it passes the static rules. */
export function checkBlockCode(code: string): { id: string; message: string }[] {
  const problems: { id: string; message: string }[] = [];

  for (const spec of importSpecifiers(code)) {
    if (!(BLOCK_IMPORT_ALLOWLIST as readonly string[]).includes(spec)) {
      problems.push({
        id: 'DUN-B100',
        message: `import "${spec}" is not allowed (allowed: ${BLOCK_IMPORT_ALLOWLIST.join(', ')})`,
      });
    }
  }

  for (const rule of BLOCK_CODE_RULES) {
    if (rule.pattern.test(code)) {
      problems.push({ id: rule.id, message: rule.message });
    }
  }

  if (!/export\s+default\b/.test(code)) {
    problems.push({ id: 'DUN-B109', message: 'block code needs a default export (the component)' });
  }

  return problems;
}
